// Hooks for supplier task management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { SupplierTask } from '../components/suppliers/TaskCard'

export interface SupplierEvent {
  id: string
  name: string
  date: string
  venue: string
  status: 'draft' | 'active' | 'completed' | 'cancelled'
}

interface TaskAcknowledgment {
  taskId: string
  notes?: string
  acknowledgedAt: string
}

// Hook for fetching supplier events
export const useSupplierEvents = () => {
  return useQuery<SupplierEvent[]>({
    queryKey: ['supplier', 'events'],
    queryFn: async () => {
      const response = await apiClient.get('/supplier/events')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000
  })
}

// Hook for fetching supplier tasks for a specific event
export const useSupplierTasks = (eventId: string) => {
  const queryClient = useQueryClient()

  const tasksQuery = useQuery<SupplierTask[]>({
    queryKey: ['supplier', 'tasks', eventId],
    queryFn: async () => {
      if (!eventId) return []
      const response = await apiClient.get(`/supplier/events/${eventId}/tasks`)
      return response.data
    },
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
    retryDelay: 1000
  })

  const acknowledgeMutation = useMutation({
    mutationFn: async ({ taskId, notes }: { taskId: string; notes?: string }) => {
      const response = await apiClient.post(`/supplier/tasks/${taskId}/acknowledge`, {
        notes: notes?.trim() || undefined
      })
      return response.data
    },
    onSuccess: (data: TaskAcknowledgment, variables) => {
      // Update the specific task in the cache
      queryClient.setQueryData<SupplierTask[]>(
        ['supplier', 'tasks', eventId],
        (oldTasks) => {
          if (!oldTasks) return []
          
          return oldTasks.map(task => 
            task.id === variables.taskId 
              ? {
                  ...task,
                  acknowledged: true,
                  acknowledgedAt: data.acknowledgedAt,
                  notes: variables.notes
                }
              : task
          )
        }
      )

      // Invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries({ 
        queryKey: ['supplier', 'tasks', eventId] 
      })
    },
    onError: (error) => {
      console.error('Task acknowledgment failed:', error)
      // Error handling could be enhanced with toast notifications
    }
  })

  return {
    data: tasksQuery.data,
    isLoading: tasksQuery.isLoading,
    error: tasksQuery.error,
    refetch: tasksQuery.refetch,
    acknowledgeTask: (taskId: string, notes?: string) => 
      acknowledgeMutation.mutateAsync({ taskId, notes }),
    isAcknowledging: acknowledgeMutation.isPending
  }
}

// Hook for fetching location information for an event
export const useEventLocation = (eventId: string) => {
  return useQuery({
    queryKey: ['supplier', 'location', eventId],
    queryFn: async () => {
      if (!eventId) return null
      const response = await apiClient.get(`/supplier/events/${eventId}/location`)
      return response.data
    },
    enabled: !!eventId,
    staleTime: 10 * 60 * 1000, // 10 minutes - location data rarely changes
    retry: 1
  })
}

// Hook for fetching compliance requirements for an event  
export const useEventCompliance = (eventId: string) => {
  return useQuery({
    queryKey: ['supplier', 'compliance', eventId],
    queryFn: async () => {
      if (!eventId) return null
      const response = await apiClient.get(`/supplier/events/${eventId}/compliance`)
      return response.data
    },
    enabled: !!eventId,
    staleTime: 15 * 60 * 1000, // 15 minutes - compliance data is relatively static
    retry: 1
  })
}